/**
 * Custom hook for rendering action dots on a D3 basketball court.
 * Simplified version of useHexbinChart — no hexbin aggregation, no color/size scales.
 * Each action is rendered as an individual dot on the court.
 *
 * Handles:
 * - Court drawing (reuses drawCourt from ShotDetailModal/utils)
 * - Dot rendering with enter/update/exit pattern
 * - Smooth animated transitions when filters change
 * - Highlight/dim transitions when a summary row is hovered or selected
 * - Cleanup on unmount
 */

import { useEffect, useRef, useMemo } from "react";
import * as d3 from "d3";
import { Action, ActionFilters, ChanceLineup } from "./types";
import { applyActionFilters } from "./utils";
import { drawCourt, createCoordinateTransformer } from "../ShotDetailModal/utils";

// Animation constants
const TRANSITION_DURATION = 300;
const HIGHLIGHT_TRANSITION = 150;

// Dot styling
const DOT_RADIUS = 2.5;
const DOT_FILL = "#333";
const DOT_OPACITY = 0.6;

// Highlight styling
const HIGHLIGHT_OPACITY = 0.85;
const HIGHLIGHT_RADIUS = 3.0;
const DIM_OPACITY = 0.08;

interface UseActionDotChartOptions {
  width: number;
  height: number;
  showCollegeLine?: boolean;
  highlightedActions?: Set<Action> | null;
}

interface UseActionDotChartReturn {
  svgRef: React.RefObject<SVGSVGElement>;
  filteredActionCount: number;
}

/**
 * Hook to manage a D3 dot chart visualization for actions.
 *
 * @param actions - Array of action data to visualize
 * @param filters - Current filter state
 * @param options - Chart configuration options
 * @returns Object containing SVG ref and filtered action count
 */
export function useActionDotChart(
  actions: Action[],
  filters: ActionFilters,
  lineups: Record<string, ChanceLineup>,
  options: UseActionDotChartOptions
): UseActionDotChartReturn {
  const { width, height, showCollegeLine = false, highlightedActions = null } = options;
  const svgRef = useRef<SVGSVGElement>(null);
  const isInitializedRef = useRef(false);

  // Keep a ref for highlight state so the data-join effect can read it
  const highlightRef = useRef<Set<Action> | null>(null);
  useEffect(() => {
    highlightRef.current = highlightedActions;
  }, [highlightedActions]);

  // Compute filtered actions
  const filteredActions = useMemo(
    () => applyActionFilters(actions, filters, lineups),
    [actions, filters, lineups]
  );

  // Create coordinate transformer
  const transformer = useMemo(
    () => createCoordinateTransformer(width, height),
    [width, height]
  );

  // Create a stable key for each action (for D3 data joins)
  const actionKey = (action: Action, index: number): string => {
    if (action.chanceId) {
      return `${action.chanceId}-${action.orderInChance}`;
    }
    return `action-${index}`;
  };

  // Initialize SVG and draw court (once)
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || isInitializedRef.current) return;

    const svgSelection = d3.select(svg);

    // Set SVG dimensions
    svgSelection
      .attr("width", width)
      .attr("height", height)
      .attr("viewBox", `0 0 ${width} ${height}`);

    // Create court group (behind dots)
    const courtGroup = svgSelection.append("g").attr("class", "court-group");
    drawCourt(courtGroup, { width, height, showCollegeLine });

    // Create dot group (on top of court)
    svgSelection.append("g").attr("class", "dot-group");

    isInitializedRef.current = true;
  }, [width, height, showCollegeLine]);

  // Update dots when filtered actions change
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || !isInitializedRef.current) return;

    const svgSelection = d3.select(svg);
    const dotGroup = svgSelection.select(".dot-group");

    // Read current highlight state for entering dots
    const hl = highlightRef.current;
    const hasHighlight = hl && hl.size > 0;

    // Data join with keyed function
    const dots = dotGroup
      .selectAll<SVGCircleElement, Action>(".action-dot")
      .data(filteredActions, (d: any, i: number) => actionKey(d, i));

    // EXIT: fade out and shrink
    dots
      .exit()
      .transition()
      .duration(TRANSITION_DURATION)
      .attr("r", 0)
      .style("opacity", 0)
      .remove();

    // ENTER: new dots grow in (respect current highlight state)
    const entering = dots
      .enter()
      .append("circle")
      .attr("class", "action-dot")
      .attr("cx", (d: Action) => transformer.translateX(d.x))
      .attr("cy", (d: Action) => transformer.translateY(d.y))
      .attr("r", 0)
      .style("fill", DOT_FILL)
      .style("opacity", 0);

    entering
      .transition()
      .duration(TRANSITION_DURATION)
      .attr("r", (d: Action) =>
        hasHighlight ? (hl!.has(d) ? HIGHLIGHT_RADIUS : DOT_RADIUS) : DOT_RADIUS
      )
      .style("opacity", (d: Action) =>
        hasHighlight ? (hl!.has(d) ? HIGHLIGHT_OPACITY : DIM_OPACITY) : DOT_OPACITY
      );

    // UPDATE: transition existing dots to new positions (in case data reorders)
    dots
      .transition()
      .duration(TRANSITION_DURATION)
      .attr("cx", (d: Action) => transformer.translateX(d.x))
      .attr("cy", (d: Action) => transformer.translateY(d.y))
      .attr("r", (d: Action) =>
        hasHighlight ? (hl!.has(d) ? HIGHLIGHT_RADIUS : DOT_RADIUS) : DOT_RADIUS
      )
      .style("opacity", (d: Action) =>
        hasHighlight ? (hl!.has(d) ? HIGHLIGHT_OPACITY : DIM_OPACITY) : DOT_OPACITY
      );
  }, [filteredActions, transformer]);

  // Highlight effect — separate from data join for smooth transitions
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || !isInitializedRef.current) return;

    const dotGroup = d3.select(svg).select(".dot-group");
    const hasHighlight = highlightedActions && highlightedActions.size > 0;

    dotGroup
      .selectAll<SVGCircleElement, Action>(".action-dot")
      .transition()
      .duration(HIGHLIGHT_TRANSITION)
      .style("opacity", (d: Action) => {
        if (!hasHighlight) return DOT_OPACITY;
        return highlightedActions!.has(d) ? HIGHLIGHT_OPACITY : DIM_OPACITY;
      })
      .attr("r", (d: Action) => {
        if (!hasHighlight) return DOT_RADIUS;
        return highlightedActions!.has(d) ? HIGHLIGHT_RADIUS : DOT_RADIUS;
      });
  }, [highlightedActions]);

  // Cleanup on unmount
  useEffect(() => {
    const svg = svgRef.current;
    return () => {
      if (svg) {
        d3.select(svg).selectAll("*").remove();
      }
      isInitializedRef.current = false;
    };
  }, []);

  return {
    svgRef,
    filteredActionCount: filteredActions.length,
  };
}
